/* Core & libraries */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTheme } from "react-jss";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { Formik } from "formik";
import { Field, ErrorMessage } from "formik";
import { Link, useLocation, useNavigate, matchPath } from "react-router-dom";

/* Helpers, constants, custom methods, internal libraries */
import {
  createValidationPolicyYupSchema,
  useValidationService,
} from "../../../resources/validations/validations.js";
import {
  ADD_SPECIFIC_POLICY_ROUTE,
  EDIT_POLICY_ROUTE,
  POLICY_ROUTE,
} from "../../../constants/routes.js";
import { usePolicyService } from "../../../services/policyService.js";
import { aaPolicyCabinOptions, AIRLINES } from '../../../constants/appConstants.js';


/* Components */
import UCButton from "../../../components/buttons/index.js";
import UCSelect from "../../../components/ucselect/index.js";
import UCToggle from "../../../components/uctoggle/index.js";
import { ReactComponent as SpecificPolicyOneWay } from "../../../resources/images/specific-policy-one-way.svg";
import { ReactComponent as SpecificPolicyTwoWays } from "../../../resources/images/specific-policy-two-ways.svg";
import { ReactComponent as RemovePolicy } from "../../../resources/images/user-icon-red.svg";
import UCAutocomplete from "../../../components/ucautocomplete/index.js";
import Breadcrumbs from "../../../components/breadcrumbs/index.js";

/* Styles */
import styles, { selectField } from "./styles.js";
import { colors } from "../../../constants/colors.js";
import { useUserContext } from '../../../contexts/userContext';
import { getManagingTravelPolicyLink } from '../../../helpers/general';


const messages = defineMessages({
  searchPlaceholder: {
    id: "specificPolicy.searchPlaceholder",
  },
});

const SpecificPolicy = () => {
  const globalTheme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const intl = useIntl();
  const userDetails = useUserContext();

  const { searchLocations, editSpecificPolicy, deleteSpecificPolicy } =
    usePolicyService();

  const isOnCreatePath = matchPath(
    ADD_SPECIFIC_POLICY_ROUTE,
    location.pathname
  );

  const { specificPolicyMockedApiValidationObj } = useValidationService();
  const [specificPolicySchema, setSpecificPolicySchema] = useState({});
  const [fromLocationPadding, setFromLocationPadding] = useState(0);
  const [toLocationPadding, setToLocationPadding] = useState(0);

  const fromSpanRef = useRef();
  const toSpanRef = useRef();
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;

  const managingTravelPolicyLink = useMemo(
    () => getManagingTravelPolicyLink(providerCode),
    [providerCode],
  );
  const durationCabinSubtitle = intl.formatMessage(
    {
      id:
        providerCode === AIRLINES.AS
          ? 'specificPolicy.cabinSubitle.AS'
          : 'specificPolicy.cabinSubitle',
    },
    {
      link: (link) => `<a href="${managingTravelPolicyLink}" target="_blank">${link}</a>`,
    },
  );

  useEffect(() => {
    if (fromSpanRef.current) {
      setFromLocationPadding(fromSpanRef?.current?.offsetWidth + 50);
    }
    if (toSpanRef.current) {
      setToLocationPadding(toSpanRef?.current?.offsetWidth + 50);
    }
  }, []);

  const theme = {
    ...globalTheme,
    fromLocationPadding,
    toLocationPadding,
  };
  const classes = styles({ theme });


  useEffect(() => {
    //TODO: api call to get validations //simpleMockedApiValidationObj
    const result = createValidationPolicyYupSchema(
      specificPolicyMockedApiValidationObj
    );
    setSpecificPolicySchema(result);
  }, []);

  const [options, setOptions] = useState(null);

  const formRef = useRef(null);

  const handleFormSubmit = () => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  };

  const submitForm = (values, setSubmitting) => {
    const data = {
      ...values,
      policyRuleId: isOnCreatePath ? null : location.state?.data?.policyRuleId,
    };
    editSpecificPolicy(
      data,
      location.state?.policyId,
      isOnCreatePath
    );
    setSubmitting(false);
  };

  const cancelUpdate = () => {
    if (formRef.current) {
      formRef.current.resetForm();
    }
    navigate(
      "../" + EDIT_POLICY_ROUTE.replace(":policyId", location.state?.policyId),
      {
        state: {
          policyId: location.state?.policyId,
          showSaveBtn: location.state?.showSaveBtn
        },
      }
    );
  };

  const getOptions = async (term, spinnerName) => {
    const response = await searchLocations(term, spinnerName);
    if (term.length < 3) {
      setOptions(null);
    } else {
      const transformResponseToArray = (response) => {
        const result = [];
        if (response.cities) {
          response.cities.forEach((city) => {
            const cityObject = {
              key: city.key,
              name: city.value,
              type: "City",
              airports: [],
            };
            if (response.airports) {
              response.airports.forEach((airport) => {
                if (airport.airport.cityCode === city.key) {
                  cityObject.airports.push({
                    key: airport.key,
                    name: airport.value,
                    type: "Airport",
                  });
                }
              });
            }
            result.push(cityObject);
          });
        }
        if (response.airports) {
          response.airports.forEach((airport) => {
            const cityExists =
              response.cities &&
              response.cities.some(
                (city) => city.key === airport.airport.cityCode
              );
            if (!cityExists) {
              result.push({
                key: airport.key,
                name: airport.value,
                type: "Airport",
              });
            }
          });
        }
        if (response.countries) {
          response.countries.forEach((country) => {
            result.push({
              key: country.key,
              name: country.value,
              type: "Country",
            });
          });
        }
        if (response.regions) {
          response.regions.forEach((region) => {
            result.push({
              key: region.key,
              name: region.value,
              type: "Region",
            });
          });
        }
        return result;
      };
      setOptions(transformResponseToArray(response));
    }
  };

  const capitalizeFirstLetter = (str) => {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const toUpperCase = (str) => {
    if (!str) return "";
    return str.toUpperCase();
  };

  return (
    <div>
      <div className={classes.pageHeader}>
        <Breadcrumbs
          items={[
            <Link to={POLICY_ROUTE}>
              <FormattedMessage id="breadcrumbs.policy" />
            </Link>,
            <Link
              to={'../' + EDIT_POLICY_ROUTE.replace(':policyId', location.state?.policyId)}
              state={{
                policyId: location.state?.policyId,
                showSaveBtn: location.state?.showSaveBtn,
              }}
            >
              {location.state?.policyName}
            </Link>,
            !!isOnCreatePath ? (
              <FormattedMessage
                id={
                  providerCode === AIRLINES.AS
                    ? 'specificPolicy.createTitle.AS'
                    : 'specificPolicy.createTitle'
                }
              />
            ) : (
              <FormattedMessage
                id={
                  providerCode === AIRLINES.AS
                    ? 'specificPolicy.editTitle.AS'
                    : 'specificPolicy.editTitle'
                }
              />
            ),
          ]}
        />
        <Container className={classes.container}>
          <Row>
            <Col xs={12} className="gx-0">
              <h1 className={classes.title}>
                {!!isOnCreatePath ? (
                  <FormattedMessage
                    id={
                      providerCode === AIRLINES.AS
                        ? 'specificPolicy.createTitle.AS'
                        : 'specificPolicy.createTitle'
                    }
                  />
                ) : (
                  <FormattedMessage
                    id={
                      providerCode === AIRLINES.AS
                        ? 'specificPolicy.editTitle.AS'
                        : 'specificPolicy.editTitle'
                    }
                  />
                )}
              </h1>
            </Col>
          </Row>
        </Container>
      </div>
      <hr className={classes.divider} />
      <Formik
        innerRef={formRef}
        initialValues={{
          from: location.state?.data?.from || '',
          fromLocationName: location.state?.data?.fromLocationName || '',
          fromLocationType: location.state?.data?.fromLocationType || '',
          to: location.state?.data?.to || '',
          toLocationName: location.state?.data?.toLocationName || '',
          toLocationType: location.state?.data?.toLocationType || '',
          biDirectional:
            location.state?.data?.biDirectional !== undefined
              ? location.state?.data?.biDirectional
              : true,
          classOfService: location.state?.data?.classOfService || '3',
        }}
        validationSchema={specificPolicySchema}
        onSubmit={(values, { setSubmitting }) => {
          submitForm(values, setSubmitting);
        }}
      >
        {({
          values,
          errors,
          touched,
          setFieldTouched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue,
          validateForm,
        }) => {
          return (
            <>
              <form onSubmit={handleSubmit}>
                <div className={classes.policyContainer}>
                  <h3>
                    <FormattedMessage
                      id={
                        providerCode === AIRLINES.AS
                          ? 'specificPolicy.title.AS'
                          : 'specificPolicy.title'
                      }
                    />
                  </h3>
                  <p>
                    <FormattedMessage
                      id={
                        providerCode === AIRLINES.AS
                          ? 'specificPolicy.subtitle.AS'
                          : 'specificPolicy.subtitle'
                      }
                    />
                  </p>
                  <div className={classes.policyCard}>
                    <div className={classes.policyCardRow}>
                      <div className={classes.policyRowName}>
                        <h6>
                          <FormattedMessage id="specificPolicy.locationsTitle" />
                        </h6>
                        <p>
                          <FormattedMessage
                            id={
                              providerCode === AIRLINES.AS
                                ? 'specificPolicy.locationsSubtitle.AS'
                                : 'specificPolicy.locationsSubtitle'
                            }
                          />
                        </p>
                      </div>
                      <div className={classes.policyRowAction}>
                        <div className={classes.policySearchWrapper}>
                          <Field type="input" name="fromLocationName" id="fromLocationName">
                            {() => (
                              <div
                                className={`${classes.policyAutocomplete} ${
                                  values.fromLocationName ? classes.fromEditMode : ''
                                }`}
                              >
                                <UCAutocomplete
                                  value={
                                    values.fromLocationType === 'CITY' ||
                                    values.fromLocationType === 'AIRPORT'
                                      ? `${values.fromLocationName} (${values.from})`
                                      : values.fromLocationName
                                  }
                                  type="POLICY"
                                  placeholder={intl.formatMessage(messages.searchPlaceholder)}
                                  options={options}
                                  getOptions={getOptions}
                                  onSelectOption={(item) => {
                                    setFieldValue('from', item.key);
                                    setFieldValue('fromLocationName', item.name);
                                    setFieldValue('fromLocationType', toUpperCase(item.type));
                                    setTimeout(() => {
                                      validateForm();
                                      setFromLocationPadding(
                                        fromSpanRef?.current?.offsetWidth + 50,
                                      );
                                    }, 0);
                                    setOptions(null);
                                  }}
                                  onClear={() => {
                                    setFieldValue('from', '');
                                    setFieldValue('fromLocationName', '');
                                    setFieldValue('fromLocationType', '');
                                  }}
                                  inputStyling={
                                    errors.fromLocationName && touched.fromLocationName
                                      ? classes.inputError
                                      : values.fromLocationType === 'AIRPORT' &&
                                          values.toLocationType === 'AIRPORT' &&
                                          values.fromLocationName === values.toLocationName
                                        ? classes.inputLocationsError
                                        : classes.inputAutocomplete
                                  }
                                  optionsStyling={classes.autocompleteOptions}
                                  shouldFetchOptionsProp={false}
                                  spinnerName="searchLocationsFrom"
                                />
                                <div className={classes.errorWrapper}>
                                  <ErrorMessage name="fromLocationName" component="span" />
                                </div>
                                <span className={classes.selectionType} ref={fromSpanRef}>
                                  {values.fromLocationType && (
                                    <FormattedMessage
                                      id={`specificPolicy.locationType.${capitalizeFirstLetter(
                                        values.fromLocationType,
                                      )}`}
                                    />
                                  )}
                                </span>
                              </div>
                            )}
                          </Field>
                          {values.biDirectional ? (
                            <SpecificPolicyTwoWays />
                          ) : (
                            <SpecificPolicyOneWay />
                          )}
                          <Field type="select" name="toLocationName" id="toLocationName">
                            {() => (
                              <div
                                className={`${classes.policyAutocomplete} ${
                                  values.toLocationName ? classes.toEditMode : ''
                                }`}
                              >
                                <UCAutocomplete
                                  value={
                                    values.toLocationType === 'CITY' ||
                                    values.toLocationType === 'AIRPORT'
                                      ? `${values.toLocationName} (${values.to})`
                                      : values.toLocationName
                                  }
                                  type="POLICY"
                                  placeholder={intl.formatMessage(messages.searchPlaceholder)}
                                  options={options}
                                  getOptions={getOptions}
                                  onSelectOption={(item) => {
                                    setFieldValue('to', item.key);
                                    setFieldValue('toLocationName', item.name);
                                    setFieldValue('toLocationType', toUpperCase(item.type));
                                    setTimeout(() => {
                                      validateForm();
                                      setToLocationPadding(toSpanRef?.current?.offsetWidth + 50);
                                    }, 0);
                                    setOptions(null);
                                  }}
                                  onClear={() => {
                                    setFieldValue('to', '');
                                    setFieldValue('toLocationName', '');
                                    setFieldValue('toLocationType', '');
                                  }}
                                  inputStyling={
                                    errors.toLocationName && touched.toLocationName
                                      ? classes.inputError
                                      : values.fromLocationType === 'AIRPORT' &&
                                          values.toLocationType === 'AIRPORT' &&
                                          values.fromLocationName === values.toLocationName
                                        ? classes.inputLocationsError
                                        : classes.inputAutocomplete
                                  }
                                  optionsStyling={classes.autocompleteOptions}
                                  shouldFetchOptionsProp={false}
                                  spinnerName="searchLocationsTo"
                                />
                                <div className={classes.errorWrapper}>
                                  <ErrorMessage name="toLocationName" component="span" />
                                </div>
                                <span className={classes.selectionType} ref={toSpanRef}>
                                  {values.toLocationType && (
                                    <FormattedMessage
                                      id={`specificPolicy.locationType.${capitalizeFirstLetter(
                                        values.toLocationType,
                                      )}`}
                                    />
                                  )}
                                </span>
                              </div>
                            )}
                          </Field>
                        </div>
                        {values.fromLocationType === 'AIRPORT' &&
                          values.toLocationType === 'AIRPORT' &&
                          values.fromLocationName === values.toLocationName && (
                            <div className={classes.locationsError}>
                              <FormattedMessage id="specificPolicy.airportsError" />
                            </div>
                          )}
                        <div className={classes.toggleLabel}>
                          <UCToggle
                            label={<FormattedMessage id="specificPolicy.locationsToggle" />}
                            icons={null}
                            onChange={(e) => {
                              setFieldValue('biDirectional', e.target.checked);
                            }}
                            defaultChecked={values.biDirectional}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={classes.policyCard}>
                    <div className={classes.policyCardRow}>
                      <div className={classes.policyRowName}>
                        <h6>
                          <FormattedMessage
                            id={
                              providerCode === AIRLINES.AS
                                ? 'specificPolicy.cabinTitle.AS'
                                : 'specificPolicy.cabinTitle'
                            }
                          />
                        </h6>
                        <div className={classes.policyTextLink}>
                          <p
                            dangerouslySetInnerHTML={{
                              __html: durationCabinSubtitle,
                            }}
                          />
                          {/* <Link to={"#"} target="_blank"> */}
                          {/* <FormattedMessage id="editPolicy.durationCabinLink" /> */}
                          {/* </Link> */}
                        </div>
                      </div>
                      <div className={classes.policyRowAction}>
                        <Field type="select" name="classOfService" id="classOfService">
                          {({ form }) => (
                            <div className={classes.selectFieldWrapper}>
                              <UCSelect
                                styles={selectField}
                                className={classes.selectField}
                                options={aaPolicyCabinOptions}
                                value={values.classOfService}
                                defaultValue={aaPolicyCabinOptions.find(
                                  (item) => item.value === values.classOfService,
                                )}
                                onChange={(item) =>
                                  form.setFieldValue('classOfService', item.value)
                                }
                              />
                              <ErrorMessage
                                name="classOfService"
                                component="span"
                                className={classes.error}
                              />
                            </div>
                          )}
                        </Field>
                      </div>
                    </div>
                  </div>
                  <hr className={classes.divider} />
                  <div className={classes.btnContainer}>
                    {!!isOnCreatePath ? (
                      <div />
                    ) : (
                      <UCButton
                        type="deleteLink"
                        className={classes.removeBtn}
                        text={<FormattedMessage id="specificPolicy.removeBtn" />}
                        onClick={() =>
                          deleteSpecificPolicy(
                            location.state?.policyId,
                            location.state?.data?.policyRuleId,
                          )
                        }
                        icon={<RemovePolicy />}
                      />
                    )}
                    <div>
                      <UCButton
                        type="default"
                        className={classes.cancelBtn}
                        onClick={cancelUpdate}
                        text={<FormattedMessage id="specificPolicy.cancelBtn" />}
                      />
                      <UCButton
                        type="primary"
                        className={classes.saveBtn}
                        onClick={handleFormSubmit}
                        text={<FormattedMessage id="specificPolicy.saveBtn" />}
                        spinner={{
                          name: 'editSpecificPolicySpinner',
                          size: 20,
                          color: colors.white,
                        }}
                        disabled={
                          values.fromLocationType === 'AIRPORT' &&
                          values.toLocationType === 'AIRPORT' &&
                          values.fromLocationName === values.toLocationName
                        }
                      />
                    </div>
                  </div>
                </div>
              </form>
            </>
          );
        }}
      </Formik>
    </div>
  );
};

export default SpecificPolicy;
